import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getFinancialNodes } from 'lib/api/accounting';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomAutocomplete from 'lib/designSystem/CustomAutocomplete';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  assignSupplierAccount,
  deleteSupplierAccount,
} from '../../../../../../lib/api';

interface SupplierAccount {
  id: number;
  businessIdentifier: string;
  financialStatementsTreeNodesId: number;
  createdAt: string;
  updatedAt: string;
  financialStatementsTreeNodes: {
    id: number;
    key: string;
    accountCode: string;
    accountCodeType: string;
    accountAuxiliaryCode: string;
  };
}

interface FinancialNode {
  id: number;
  key: string;
  accountCode: string;
  accountCodeType?: string;
  accountAuxiliaryCode?: string;
}

interface SupplierAccountsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  supplierIdentifier: string;
  supplierName: string;
  initialAccounts?: SupplierAccount[];
  onRefresh?: () => void;
}

const SupplierAccounts: React.FC<SupplierAccountsProps> = ({
  open,
  setOpen,
  supplierIdentifier,
  supplierName,
  initialAccounts = [],
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<SupplierAccount[]>([]);
  const [availableNodes, setAvailableNodes] = useState<FinancialNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<FinancialNode | null>(null);
  const [loadingNodes, setLoadingNodes] = useState(false);

  const fetchAvailableNodes = async () => {
    setLoadingNodes(true);
    try {
      const nodes = await getFinancialNodes(dispatch);
      // Filtrar las cuentas que ya están asignadas
      const assignedNodeIds = accounts.map(
        (acc) => acc.financialStatementsTreeNodesId,
      );
      const filteredNodes = (nodes || []).filter(
        (node: FinancialNode) => !assignedNodeIds.includes(node.id),
      );
      setAvailableNodes(filteredNodes);
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al cargar las cuentas disponibles');
    }
    setLoadingNodes(false);
  };

  // Inicializar y actualizar cuentas desde las props
  useEffect(() => {
    if (open && initialAccounts) {
      setAccounts(initialAccounts);
    }
  }, [open, initialAccounts]);

  useEffect(() => {
    if (open && accounts.length >= 0) {
      void fetchAvailableNodes();
    }
  }, [open, accounts]);

  const handleAssign = async () => {
    if (!selectedNode) {
      showAlert(dispatch, 'error', 'Debes seleccionar una cuenta');
      return;
    }

    // Verificar que la cuenta no esté ya asignada
    const isAlreadyAssigned = accounts.some(
      (acc) => acc.financialStatementsTreeNodesId === selectedNode.id,
    );
    if (isAlreadyAssigned) {
      showAlert(dispatch, 'error', 'Esta cuenta ya está asignada');
      return;
    }

    setLoading(true);
    try {
      await assignSupplierAccount(dispatch, supplierIdentifier, {
        financialStatementsTreeNodesId: selectedNode.id,
      });
      showAlert(dispatch, 'success', 'Cuenta contable asignada correctamente');
      setSelectedNode(null);

      // Refrescar la lista de proveedores para obtener las cuentas actualizadas
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al asignar la cuenta contable');
    }
    setLoading(false);
  };

  const handleUnassign = async (accountId: number) => {
    if (!window.confirm('¿Estás seguro de desasignar esta cuenta contable?')) {
      return;
    }
    setLoading(true);
    try {
      await deleteSupplierAccount(dispatch, supplierIdentifier, accountId);
      showAlert(
        dispatch,
        'success',
        'Cuenta contable desasignada correctamente',
      );
      // Remover la cuenta del estado local
      setAccounts((prev) => prev.filter((acc) => acc.id !== accountId));
      // Refrescar la lista de proveedores para mantener sincronización
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al desasignar la cuenta contable');
    }
    setLoading(false);
  };

  // Función de filtrado para el autocomplete
  const filterNodeOptions = (
    options: FinancialNode[],
    inputValue: string,
  ): FinancialNode[] => {
    if (!inputValue) {
      return options;
    }

    const searchText = inputValue.toLowerCase().trim();

    // Separar opciones por tipo de coincidencia
    const exactMatches: FinancialNode[] = [];
    const startsWithMatches: FinancialNode[] = [];
    const containsMatches: FinancialNode[] = [];

    options.forEach((option) => {
      const accountCode = option.accountCode?.toLowerCase() || '';
      const key = option.key?.toLowerCase() || '';

      // Coincidencia exacta en accountCode o key
      if (accountCode === searchText || key === searchText) {
        exactMatches.push(option);
      }
      // Empieza con el texto en accountCode o key
      else if (
        accountCode.startsWith(searchText) ||
        key.startsWith(searchText)
      ) {
        startsWithMatches.push(option);
      }
      // Contiene el texto en accountCode o key
      else if (accountCode.includes(searchText) || key.includes(searchText)) {
        containsMatches.push(option);
      }
    });

    // Retornar ordenado: exactas primero, luego las que empiezan, luego las que contienen
    return [...exactMatches, ...startsWithMatches, ...containsMatches];
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Cuentas Contables - {supplierName}</Typography>
      </DialogTitle>
      <DialogContent>
        {loading && accounts.length === 0 ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <>
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Asignar nueva cuenta contable
              </Typography>
              <Box display="flex" gap={2} alignItems="flex-end" mt={1}>
                <Box flex={1}>
                  <CustomAutocomplete
                    options={availableNodes}
                    value={selectedNode}
                    onChange={(newValue) => setSelectedNode(newValue)}
                    getOptionLabel={(option) =>
                      `${option.accountCode} - ${option.key}`
                    }
                    filterOptions={filterNodeOptions}
                    label="Buscar cuenta contable"
                    placeholder="Buscar por código o nombre..."
                    fullWidth
                    disabled={loading || loadingNodes}
                  />
                </Box>
                <CustomButton
                  color="primary"
                  onClick={handleAssign}
                  disabled={loading || !selectedNode || loadingNodes}
                  startIcon={<Add />}
                >
                  Asignar
                </CustomButton>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No hay cuentas contables asignadas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>
                          {account.financialStatementsTreeNodes.accountCode}
                        </TableCell>
                        <TableCell>
                          {account.financialStatementsTreeNodes.key}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              void handleUnassign(account.id);
                            }}
                            disabled={loading}
                            color="error"
                            title="Desasignar cuenta"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="secondary"
          onClick={() => setOpen(false)}
          disabled={loading}
        >
          Cerrar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierAccounts;
